import type { PolymorphicProps } from '@kobalte/core/polymorphic'
import type {
  TextFieldDescriptionProps,
  TextFieldErrorMessageProps,
  TextFieldInputProps,
  TextFieldLabelProps,
  TextFieldRootProps,
  TextFieldTextAreaProps,
} from '@kobalte/core/text-field'
import type { JSX, ValidComponent, VoidProps } from 'solid-js'
import { TextField as TextFieldPrimitive } from '@kobalte/core/text-field'
import { cva } from 'class-variance-authority'
import { splitProps } from 'solid-js'
import { cn } from '../../utils/cn.ts'

type textFieldProps<T extends ValidComponent = 'div'> = TextFieldRootProps<T> & {
  class?: string
}

export function TextFieldRoot<T extends ValidComponent = 'div'>(props: PolymorphicProps<T, textFieldProps<T>>) {
  const [local, rest] = splitProps(props as textFieldProps, ['class'])

  return <TextFieldPrimitive class={cn('space-y-2', local.class)} {...rest} />
}

export const textfieldLabel = cva('text-sm data-[disabled]:(cursor-not-allowed opacity-70) font-medium', {
  variants: {
    label: {
      true: 'data-[invalid]:text-destructive',
    },
    error: {
      true: 'text-destructive text-xs',
    },
    description: {
      true: 'font-normal text-muted-foreground',
    },
  },
  defaultVariants: {
    label: true,
  },
})

type textFieldLabelProps<T extends ValidComponent = 'label'> = TextFieldLabelProps<T> & {
  class?: string
}

export function TextFieldLabel<T extends ValidComponent = 'label'>(props: PolymorphicProps<T, textFieldLabelProps<T>>) {
  const [local, rest] = splitProps(props as textFieldLabelProps, ['class'])

  return <TextFieldPrimitive.Label class={cn(textfieldLabel(), local.class)} {...rest} />
}

type textFieldErrorMessageProps<T extends ValidComponent = 'div'> = TextFieldErrorMessageProps<T> & {
  class?: string
}

export function TextFieldErrorMessage<T extends ValidComponent = 'div'>(
  props: PolymorphicProps<T, textFieldErrorMessageProps<T>>,
) {
  const [local, rest] = splitProps(props as textFieldErrorMessageProps, ['class'])

  return <TextFieldPrimitive.ErrorMessage class={cn(textfieldLabel({ error: true }), local.class)} {...rest} />
}

type textFieldDescriptionProps<T extends ValidComponent = 'div'> = TextFieldDescriptionProps<T> & {
  class?: string
}

export function TextFieldDescription<T extends ValidComponent = 'div'>(
  props: PolymorphicProps<T, textFieldDescriptionProps<T>>,
) {
  const [local, rest] = splitProps(props as textFieldDescriptionProps, ['class'])

  return <TextFieldPrimitive.Description class={cn(textfieldLabel({ description: true }), local.class)} {...rest} />
}

export const inputFrameClass = 'flex items-center h-auto w-full rounded-[10px] border border-input bg-input/30 px-3 py-1 text-sm shadow-sm focus-within:(outline-none ring-3 ring-ring/50 border-ring) transition-shadow duration-50'

interface TextFieldFrameProps {
  class?: string
  children?: JSX.Element
}

export function TextFieldFrame<T extends ValidComponent = 'div'>(props: PolymorphicProps<T, TextFieldFrameProps>) {
  const [local, rest] = splitProps(props as TextFieldFrameProps, ['class'])

  return (
    <div
      class={cn(inputFrameClass, local.class)}
      {...rest}
    />
  )
}

type textFieldInputProps<T extends ValidComponent = 'input'> = VoidProps<
  TextFieldInputProps<T> & {
    class?: string
  }
>

export function TextField<T extends ValidComponent = 'input'>(props: PolymorphicProps<T, textFieldInputProps<T>>) {
  const [local, rest] = splitProps(props as textFieldInputProps, ['class'])

  return (
    <TextFieldPrimitive.Input
      class={cn(
        'w-full outline-none bg-transparent file:(border-0 text-sm font-medium) text-sm placeholder:text-muted-foreground disabled:(cursor-not-allowed opacity-50)',
        local.class,
      )}
      {...rest}
    />
  )
}

type textAreaInputProps<T extends ValidComponent = 'textarea'> = VoidProps<
  TextFieldTextAreaProps<T> & {
    class?: string
  }
>

export function TextArea<T extends ValidComponent = 'input'>(props: PolymorphicProps<T, textAreaInputProps<T>>) {
  const [local, rest] = splitProps(props as textAreaInputProps, ['class'])

  return (
    <TextFieldPrimitive.TextArea
      class={cn(
        'w-full outline-none bg-transparent text-sm p-2 placeholder:text-muted-foreground disabled:(cursor-not-allowed opacity-50)',
        local.class,
      )}
      {...rest}
    />
  )
}
